import { checkAndFixToken } from './browserTokenCheck';
import { createProbeProgressContext, reduceProbeProgressEvent } from './adminSpeedProbeProgress';

const API_BASE_URL =
  process.env.NODE_ENV === 'production'
    ? ''
    : process.env.REACT_APP_API_URL !== undefined
      ? process.env.REACT_APP_API_URL
      : '';

function parseAdhocSpeedNdjsonLine(line) {
  const trimmed = line.trim();
  if (!trimmed) {
    return null;
  }
  try {
    const parsed = JSON.parse(trimmed);
    if (parsed && typeof parsed.event === 'string') {
      return parsed;
    }
  } catch {
    return null;
  }
  return null;
}

/**
 * NDJSON поток замера скорости (админка): POST /api/proxy-checker/speed-stream/{id}
 */
/**
 * NDJSON замер для стороннего прокси: POST /api/proxy-checker/adhoc/speed-stream
 */
export async function streamAdminAdhocProxySpeed(payload, options) {
  const token = checkAndFixToken();
  const url = `${API_BASE_URL}/api/proxy-checker/adhoc/speed-stream`;
  const body = JSON.stringify({
    host: payload.host,
    port: payload.port,
    proxy_type: payload.proxy_type,
    username: payload.username ?? null,
    password: payload.password ?? null,
  });
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body,
    signal: options.signal,
  });
  if (!response.ok) {
    let detail = `HTTP ${response.status}`;
    try {
      const data = await response.json();
      if (data && typeof data === 'object' && data.detail != null) {
        const d = data.detail;
        detail = typeof d === 'string' ? d : detail;
      }
    } catch {
      // ignore
    }
    throw new Error(detail);
  }
  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('Поток ответа недоступен');
  }
  const decoder = new TextDecoder();
  let buffer = '';
  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';
    for (const line of lines) {
      const ev = parseAdhocSpeedNdjsonLine(line);
      if (ev) {
        options.onEvent(ev);
      }
    }
  }
  const tail = buffer.trim();
  if (tail) {
    const ev = parseAdhocSpeedNdjsonLine(tail);
    if (ev) {
      options.onEvent(ev);
    }
  }
}

export async function streamAdminProxySpeed(proxyId, options) {
  const token = checkAndFixToken();
  const url = `${API_BASE_URL}/api/proxy-checker/speed-stream/${proxyId}`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: '{}',
    signal: options.signal,
  });
  if (!response.ok) {
    let detail = `HTTP ${response.status}`;
    try {
      const data = await response.json();
      if (data && typeof data === 'object' && data.detail != null) {
        const d = data.detail;
        detail = typeof d === 'string' ? d : detail;
      }
    } catch {
      // ignore
    }
    throw new Error(detail);
  }
  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('Поток ответа недоступен');
  }
  const decoder = new TextDecoder();
  let buffer = '';
  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';
    for (const line of lines) {
      const ev = parseAdhocSpeedNdjsonLine(line);
      if (ev) {
        options.onEvent(ev);
      }
    }
  }
  const tail = buffer.trim();
  if (tail) {
    const ev = parseAdhocSpeedNdjsonLine(tail);
    if (ev) {
      options.onEvent(ev);
    }
  }
}

const toAdhocProxyTypeRow = (proxyType) =>
  String(proxyType || 'http').toUpperCase().includes('SOCK') ? 'socks5' : 'http';

/**
 * Один полный замер (NDJSON до события done) — для фоновой массовой проверки без модалки.
 * @param {object} options
 * @param {AbortSignal} [options.signal]
 * @param {(ui: { percent: number; label: string }) => void} [options.onProgress]
 */
export function runAdminCheckerSpeedProbe(row, options = {}) {
  const { signal, onProgress } = options;
  const dlPeakRef = { current: 0 };
  const ulPeakRef = { current: 0 };

  return new Promise((resolve, reject) => {
    let settled = false;
    const finish = (fn) => {
      if (settled) return;
      settled = true;
      fn();
    };

    const progressCtx = createProbeProgressContext();
    if (typeof onProgress === 'function') {
      onProgress({ percent: 0, label: 'Подготовка…' });
    }

    const onEvent = (ev) => {
      if (typeof onProgress === 'function') {
        const ui = reduceProbeProgressEvent(ev, progressCtx);
        if (ui) {
          onProgress(ui);
        }
      }
      if (ev.event === 'progress') {
        if (ev.phase === 'download') {
          const raw = typeof ev.mbps === 'number' ? ev.mbps : 0;
          dlPeakRef.current = Math.max(dlPeakRef.current, raw);
        }
        if (ev.phase === 'upload') {
          const raw = typeof ev.mbps === 'number' ? ev.mbps : 0;
          ulPeakRef.current = Math.max(ulPeakRef.current, raw);
        }
      }
      if (ev.event === 'phase') {
        if (ev.phase === 'download') {
          dlPeakRef.current = 0;
        }
        if (ev.phase === 'upload') {
          ulPeakRef.current = 0;
        }
      }
      if (ev.event === 'done') {
        const d = typeof ev.download_mbps === 'number' ? ev.download_mbps : null;
        const u = typeof ev.upload_mbps === 'number' ? ev.upload_mbps : null;
        const dlPeak = dlPeakRef.current;
        const ulPeak = ulPeakRef.current;
        const outDlMax = dlPeak > 0 ? dlPeak : d;
        const outUlMax = ulPeak > 0 ? ulPeak : u;
        finish(() =>
          resolve({
            downloadMbpsAvg: d,
            uploadMbpsAvg: u,
            downloadMbpsMax: outDlMax,
            uploadMbpsMax: outUlMax,
            error: ev.error || null,
          })
        );
      }
    };

    void (async () => {
      try {
        if (row.is_external && row.external_key) {
          const portNum = Number.parseInt(String(row.port), 10);
          await streamAdminAdhocProxySpeed(
            {
              host: String(row.host || '').trim(),
              port: portNum,
              proxy_type: toAdhocProxyTypeRow(row.proxy_type),
              username: row.login || row.username || null,
              password: row.password || null,
            },
            { signal, onEvent }
          );
        } else if (row.proxy_id != null) {
          await streamAdminProxySpeed(row.proxy_id, { signal, onEvent });
        } else {
          finish(() => reject(new Error('Нет данных прокси')));
          return;
        }
        if (!settled) {
          finish(() => reject(new Error('Нет результата замера')));
        }
      } catch (e) {
        finish(() => reject(e));
      }
    })();
  });
}
